import { useState, useRef } from 'react'
import { Upload, X, FileText, Loader2, AlertCircle } from 'lucide-react'
import type { QuestionProps } from '../eform.types'
import { eformService } from '../eform.service'

export default function FileUpload({ value, onChange, error, eformToken, visitQueue }: QuestionProps) {
  const strValue = Array.isArray(value) ? value[0] ?? '' : value
  const [fileName, setFileName] = useState(strValue || '')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    if (!eformToken) {
      setUploadError('Upload unavailable — form token missing.')
      return
    }

    if (file.size > 30 * 1024 * 1024) {
      setUploadError('File is too large. Maximum size is 30 MB.')
      return
    }

    setUploading(true)
    setUploadError(null)

    try {
      // Through the visit queue: the upload carries the latest visit token, and
      // the refreshed one it returns (naming the visit's response) is kept.
      const result = visitQueue
        ? await visitQueue.run((visitToken) => eformService.uploadFile(eformToken, file, visitToken))
        : await eformService.uploadFile(eformToken, file)
      setFileName(result.filename)
      onChange(result.filename)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed. Please try again.'
      setUploadError(msg)
      if (inputRef.current) inputRef.current.value = ''
    } finally {
      setUploading(false)
    }
  }

  function clearFile() {
    setFileName('')
    onChange('')
    setUploadError(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div>
      {uploading ? (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6">
          <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
          <span className="text-sm text-slate-500">Uploading...</span>
        </div>
      ) : strValue ? (
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <FileText className="h-5 w-5 text-slate-400 shrink-0" />
          <span className="text-sm text-slate-700 truncate flex-1">{fileName || 'File uploaded'}</span>
          <button type="button" onClick={clearFile} className="text-slate-400 hover:text-red-500">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label className={`flex flex-col items-center gap-2 rounded-lg border-2 border-dashed px-6 py-8 cursor-pointer transition-colors hover:bg-slate-50 ${
          error || uploadError ? 'border-red-300' : 'border-slate-200'
        }`}>
          <Upload className="h-8 w-8 text-slate-400" />
          <span className="text-sm text-slate-500">Click to upload a file</span>
          <span className="text-xs text-slate-400">Max 30 MB</span>
          <input
            ref={inputRef}
            type="file"
            onChange={handleFile}
            className="sr-only"
          />
        </label>
      )}
      {uploadError && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
          <p className="text-sm text-red-500">{uploadError}</p>
        </div>
      )}
      {error && !uploadError && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  )
}
